import React, { useMemo, useState } from "react";

import placeholderImg from "../../../assets/images/placeholder-img.svg";
import styled from "styled-components";
import { borders, colors, fonts, links } from "../../common/mixins";
import { Calendar, Location } from "../../icons";
import { useReservationStore } from "../../../stores/useReservationStore";
import { formatPrice } from "../../../utils/Utils";
import { useOrderTotal } from "../../../pageComponents/reservationPage/hooks/useOrderTotal";
import { Ticket } from "../../../types/reservation";
import {
  getProductLines,
  getProductTotalQty,
  getTicketNumberMap,
} from "../../../pageComponents/reservationPage/utils/productQuantities";
import { Divider } from "../../../common";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";
import { useFormContext } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import { DeliveryMethod } from "../../../common/enums/DeliveryMethod";
import type { DonationCampaign } from "../../../types/reservation";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { ProductRowsSection } from "./ProductRowsSection";
import {
  ItemContent,
  ItemPrice,
  ItemRow,
  ItemTitle,
  ItemTitleWrapper,
  TicketNameWithDiscount,
  TicketSection,
} from "./shared.styles";

// Re-exported so existing importers of this module keep working; the
// definitions live in shared.styles.tsx.
export * from "./shared.styles";

const OrderInfoCard = styled.section<{ $isSummaryDrawer?: boolean }>`
  padding: 16px 16px 24px 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  ${borders.lightGrayBorder}

  ${({ $isSummaryDrawer }) =>
    $isSummaryDrawer &&
    `
    padding: 0;
    border: none;
    border-radius: 0;
  `}
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    gap: 8px;
  }
`;

const PosterWrap = styled.div`
  width: 98px;
  height: 98px;
  flex: 0 0 98px;
  background: #edf1f6;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;

  // single-line text
  min-width: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  // single-line text
  min-width: 0;
`;

const Title = styled.a`
  margin: 0;
  margin-left: 4px;
  ${links.linkDarkBold()}
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 18px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    font-size: 16px;
  }
`;

const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;

  // single-line text
  min-width: 0;
`;

const MetaIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
`;

const InfoText = styled.p`
  margin: 0;
  ${fonts.normalText()};
  // single-line text
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    font-size: 15px;
  }
`;

const DrawerTitle = styled.h1`
  margin: 0;
  ${fonts.midtitle()};
  padding-bottom: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${fonts.smallDarkGray()};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalText = styled.h3`
  margin: 0;
  ${fonts.midtitle()};
`;

const TotalValue = styled(TotalText)`
  color: ${colors.darkText};
`;

const ViewAllWrapper = styled.div`
  padding-top: 16px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    padding-top: 8px;
  }
`;

const ViewAll = styled.a`
  line-height: 16px;
  font-family: "pf_highway_sans_pro_lightRg";
  font-size: 16px;
  font-weight: 600;
  color: ${colors.primaryButton};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${colors.buttonHovered};
    text-decoration: none;
  }
  &:active {
    color: ${colors.buttonPressed};
  }

  // CSS approach to reset the color on :focus:not(:focus-visible)
  //  — this catches mouse-click focus (not keyboard focus):
  &:focus:not(:focus-visible) {
    color: ${colors.primaryButton};
    outline: none;
  }
`;

interface OrderInfoProps {
  tickets: Ticket[];
  ticketsTotal?: number;
  playTitle: string;
  venue: { name: string; address: string; city: string };
  playUrl: string;
  date: string;
  imageUrl?: string | null;
  shippingFee?: number;
  isSummaryDrawer?: boolean;
  isUnder900?: boolean;
  donationCampaign?: DonationCampaign | null;
  paidAmount?: number;
}

export function OrderInfo({
  tickets,
  ticketsTotal = 0,
  playTitle,
  venue,
  playUrl,
  date,
  imageUrl,
  shippingFee,
  isSummaryDrawer = false,
  isUnder900 = false,
  paidAmount = 0,
}: OrderInfoProps) {
  const r = useReservationResources();

  const { watch } = useFormContext<ReservationFormValues>();
  const deliveryMethodId = watch("deliveryMethodId");
  const donationAmount = watch("donationAmount") ?? 0;

  const currency = useReservationStore((s) => s.currency);
  const products = useReservationStore((s) => s.products);
  const orderedTicketIds = useReservationStore((s) => s.orderedTicketIds);
  const [ticketsExpanded, setTicketsExpanded] = useState(false);

  const visibleTickets = isSummaryDrawer
    ? tickets
    : ticketsExpanded
      ? tickets
      : tickets.slice(0, 3);
  const ticketNo = tickets.length;

  // Numbering comes from the store's delivered ticket order, not the tickets
  // prop: split order passes a user-selected subset, and the store is the same
  // authority the per-ticket stock allocation uses.
  const ticketNumberMap = useMemo(
    () => getTicketNumberMap(orderedTicketIds),
    [orderedTicketIds],
  );

  const lines = useMemo(() => getProductLines(products), [products]);
  const perOrderRows = useMemo(
    () => lines.filter((l) => l.ticketId === null),
    [lines],
  );
  const perTicketRows = useMemo(
    () =>
      lines
        .filter((l) => l.ticketId !== null && ticketNumberMap.has(l.ticketId))
        .sort((a, b) => {
          const ticketA = ticketNumberMap.get(a.ticketId as number) ?? 0;
          const ticketB = ticketNumberMap.get(b.ticketId as number) ?? 0;
          if (ticketA !== ticketB) return ticketA - ticketB;
          if (a.product.name !== b.product.name) {
            return a.product.name.localeCompare(b.product.name);
          }
          return a.option.name.localeCompare(b.option.name);
        }),
    [lines, ticketNumberMap],
  );

  // Two product sections stacked at 3 rows each push the order total off screen
  // on a phone, so both tighten to 2 when the order has both kinds. Tickets are
  // unaffected — they keep 3 whatever the products do.
  const productCollapsedCount =
    perOrderRows.length > 0 && perTicketRows.length > 0 ? 2 : 3;

  // Only count products that have at least one unit added anywhere in the
  // order — per-ticket products can be digital too, so they must feed into
  // allProductsDigital (which hides the delivery-method row).
  const addedProducts = useMemo(
    () => products.filter((p) => getProductTotalQty(p) > 0),
    [products],
  );
  const allProductsDigital =
    addedProducts.length > 0 && addedProducts.every((p) => p.isDigital);

  const total = useOrderTotal(ticketsTotal, donationAmount, shippingFee ?? 0);

  // Show extras (donations, shipping, total) everywhere except the mobile card view
  const showExtras = isSummaryDrawer || !isUnder900;

  const image = imageUrl ? imageUrl : placeholderImg;

  return (
    <OrderInfoCard id="order-info" $isSummaryDrawer={isSummaryDrawer}>
      {!isSummaryDrawer && (
        <>
          <Header>
            <PosterWrap>
              <img src={image} alt="Event" />
            </PosterWrap>

            <InfoContainer>
              <Title href={playUrl} target="_blank" rel="noopener noreferrer">
                {playTitle}
              </Title>
              <FlexContainer>
                <MetaRow>
                  <MetaIcon>
                    <Calendar />
                  </MetaIcon>
                  <InfoText>{date}</InfoText>
                </MetaRow>
                <MetaRow>
                  <MetaIcon>
                    <Location />
                  </MetaIcon>
                  <InfoText title={venue.name}>
                    {venue.name} - {venue.address}, {venue.city}
                  </InfoText>
                </MetaRow>
              </FlexContainer>
            </InfoContainer>
          </Header>

          <Divider />
        </>
      )}

      <Container id="order-details">
        {isSummaryDrawer && <DrawerTitle>{r.PriceDetails}</DrawerTitle>}
        <SectionTitle id="tickets-title">
          {r.Tickets}
          {!isSummaryDrawer && ` (${ticketNo})`}
        </SectionTitle>
        {visibleTickets.map((ticket) => (
          <div id="ticket-item" key={ticket.id}>
            <ItemRow>
              <ItemContent>
                {ticket.discount.name ? (
                  <TicketNameWithDiscount
                    name={ticket.priceListItem?.name ?? "Ticket"}
                    discountName={ticket.discount.name}
                  />
                ) : (
                  <ItemTitleWrapper>
                    <ItemTitle id="ticket-name">
                      {ticket.priceListItem?.name ?? "Ticket"}
                    </ItemTitle>
                  </ItemTitleWrapper>
                )}
              </ItemContent>

              <ItemPrice id="ticket-price">
                {formatPrice(ticket.price ?? 0, currency)}
              </ItemPrice>
            </ItemRow>
            {ticket.seat?.isNumbered && (
              <TicketSection>
                Section {ticket.seat.section} - {ticket.seat.row}/
                {ticket.seat.number}
              </TicketSection>
            )}
          </div>
        ))}
      </Container>

      {!isSummaryDrawer && tickets.length > 3 && (
        <ViewAllWrapper id="view-all-tickets-wrapper">
          <ViewAll
            id="view-all-tickets-btn"
            role="button"
            onClick={() => setTicketsExpanded((s) => !s)}
          >
            {ticketsExpanded ? r.ViewLessTickets : r.ViewAllTickets}
          </ViewAll>
        </ViewAllWrapper>
      )}

      {(perTicketRows.length > 0 || perOrderRows.length > 0) && (
        <>
          {perTicketRows.length > 0 && (
            <>
              <Divider $margin="16px 0" />
              <ProductRowsSection
                rows={perTicketRows}
                title={r.ProductsPerTicketHolder}
                isSummaryDrawer={isSummaryDrawer}
                isUnder900={isUnder900}
                currency={currency}
                ticketNumberMap={ticketNumberMap}
                collapsedCount={productCollapsedCount}
                idSuffix="-per-ticket"
              />
            </>
          )}
          {perOrderRows.length > 0 && (
            <>
              <Divider $margin="16px 0" />
              <ProductRowsSection
                rows={perOrderRows}
                title={perTicketRows.length > 0 ? r.ProductsPerOrder : r.Products}
                isSummaryDrawer={isSummaryDrawer}
                isUnder900={isUnder900}
                currency={currency}
                collapsedCount={productCollapsedCount}
              />
            </>
          )}
        </>
      )}

      {donationAmount > 0 && showExtras && (
        <>
          <Divider $margin="16px 0" />
          <Container id="donation-order-info-section">
            <SectionTitle>{r.DonationAmount}</SectionTitle>
            <div>
              <ItemRow>
                <ItemContent>
                  <ItemTitle id="donation-amount">{r.DonationAmount}</ItemTitle>
                </ItemContent>

                <ItemPrice id="donation-price">
                  {formatPrice(donationAmount, currency)}
                </ItemPrice>
              </ItemRow>
            </div>
          </Container>
        </>
      )}

      {(deliveryMethodId === DeliveryMethod.Ship ||
        deliveryMethodId === DeliveryMethod.Collect) &&
        addedProducts.length > 0 &&
        !allProductsDigital &&
        showExtras && (
          <>
            <Divider $margin="16px 0" />
            <Container id="delivery-method-order-info-section">
              <SectionTitle>
                {deliveryMethodId === DeliveryMethod.Collect
                  ? r.DeliveryMethod
                  : r.ShippingFees}
              </SectionTitle>
              <div>
                <ItemRow>
                  <ItemContent>
                    <ItemTitle id="delivery-method">
                      {deliveryMethodId === DeliveryMethod.Collect
                        ? r.CollectAtVenue
                        : r.SendWithCourier}
                    </ItemTitle>
                  </ItemContent>

                  {deliveryMethodId !== DeliveryMethod.Collect && (
                    <ItemPrice id="delivery-method-price">
                      {formatPrice(shippingFee ?? 0, currency)}
                    </ItemPrice>
                  )}
                </ItemRow>
              </div>
            </Container>
          </>
        )}

      {showExtras && (paidAmount > 0 || !isSummaryDrawer) && (
        <>
          <Divider $margin="24px 0" />
          {paidAmount > 0 ? (
            <Container id="totals-group">
              <Footer id="total-row">
                <ItemTitle>{r.Total}</ItemTitle>
                <ItemPrice id="total-price">
                  {formatPrice(total, currency)}
                </ItemPrice>
              </Footer>
              <Footer id="already-paid-row">
                <ItemTitle>{r.AlreadyPaid || "Already Paid"}</ItemTitle>
                <ItemPrice id="already-paid-amount">
                  {`− ${formatPrice(paidAmount, currency)}`}
                </ItemPrice>
              </Footer>
              <Footer id="remaining-amount-row">
                <TotalText>{r.RemainingAmount || "Remaining amount"}</TotalText>
                <TotalValue id="remaining-amount">
                  {formatPrice(Math.max(0, total - paidAmount), currency)}
                </TotalValue>
              </Footer>
            </Container>
          ) : (
            <Footer id="total-row">
              <TotalText>{r.Total}</TotalText>
              <TotalValue id="total-price">
                {formatPrice(total, currency)}
              </TotalValue>
            </Footer>
          )}
        </>
      )}
    </OrderInfoCard>
  );
}
